import { useState, useEffect, useRef } from 'react';
import api from '../../lib/api';
import { DEFAULT_CLASS_IMAGE } from '../../lib/constants';

// In-memory cache for image URLs to avoid redundant API calls within the same session
const imageCache = new Map();

export const LazyClassImage = ({ 
  classId, 
  className = '', 
  alt = 'Class image',
  fallbackUrl = DEFAULT_CLASS_IMAGE,
  style = {},
  onLoad,
  onError
}) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const imgRef = useRef(null);

  // Check cache on mount
  useEffect(() => {
    if (classId && imageCache.has(classId)) {
      setImageUrl(imageCache.get(classId));
      setIsLoading(false);
    }
  }, [classId]);

  // Intersection Observer for lazy loading
  useEffect(() => {
    // Skip observer if already cached
    if (imageCache.has(classId)) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '100px', // Start loading 100px before entering viewport
        threshold: 0.01
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [classId]);

  // Fetch image when visible (and not cached)
  useEffect(() => {
    if (!isVisible || !classId || imageCache.has(classId)) return;

    const fetchImage = async () => {
      try {
        const response = await api.get(`/classes/${classId}/image`);
        const url = response.data?.image_url;
        if (url) {
          setImageUrl(url);
          imageCache.set(classId, url); // Cache the result
        } else {
          setImageUrl(fallbackUrl);
          imageCache.set(classId, fallbackUrl);
        }
      } catch (error) {
        console.error(`Failed to load image for class ${classId}:`, error);
        setImageUrl(fallbackUrl);
        setHasError(true);
        onError?.(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchImage();
  }, [isVisible, classId, fallbackUrl, onError]);

  const handleImageLoad = () => {
    setIsLoading(false);
    onLoad?.();
  };

  const handleImageError = () => {
    setImageUrl(fallbackUrl);
    setHasError(true);
    setIsLoading(false);
  };

  return (
    <div ref={imgRef} className={`relative ${className}`} style={style}>
      {/* Placeholder skeleton while loading */}
      {isLoading && (
        <div className="absolute inset-0 bg-[#222] animate-pulse" />
      )}
      
      {/* Actual image */}
      {(isVisible || imageUrl) && (
        <img
          src={imageUrl || fallbackUrl}
          alt={alt}
          className={`w-full h-full object-cover transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
          style={style}
          onLoad={handleImageLoad}
          onError={handleImageError}
          loading="lazy"
        />
      )}
    </div>
  );
};

export default LazyClassImage;
